import {CardEnum, CityImprovementTypes, MechanicCityImprovementsState, NestedReadonly, PlayerColor, TileCornerBonusType, TileCornerBuildingType} from '@colonist/game-states'
import {MechanicCityImprovementsPlayerState, MechanicCityImprovementsTypeState} from 'packages/game-states/src/game-state/mechanic-states/MechanicCityImprovementsState'
import {MapValidator} from '../core/map/MapValidator'
import {ActionStatus} from '@colonist/game-base-types'
import {PlayerValidator} from '../core/PlayerValidator'
import {MechanicValidator} from './MechanicValidator'
import {EnumUtils} from '@colonist/utils'

export class MechanicValidatorCityImprovements extends MechanicValidator {
    private readonly cityImprovementsState: MechanicCityImprovementsState

    constructor(playerValidators: PlayerValidator[], mapValidator: MapValidator, cityImprovementsState: MechanicCityImprovementsState) {
        super(playerValidators, mapValidator)
        this.cityImprovementsState = cityImprovementsState
    }

    getPlayerCityImprovementsState(player: PlayerColor): NestedReadonly<MechanicCityImprovementsPlayerState> {
        if(player == PlayerColor.None) return MechanicValidatorCityImprovements.getEmptyCityImprovementState()
        const playerCityImprovementsState = this.cityImprovementsState[player]
        if(playerCityImprovementsState == undefined) throw new Error(`No MechanicCityImprovementsPlayerState for player ${PlayerColor[player]}`)
        return playerCityImprovementsState
    }

    getPlayerCityImprovementsStateForType(player: PlayerColor, improvementType: CityImprovementTypes): NestedReadonly<MechanicCityImprovementsTypeState> {
        const playerCityImprovementsState = this.getPlayerCityImprovementsState(player)
        const playerCityImprovementsStateForType = playerCityImprovementsState[improvementType]
        if(playerCityImprovementsStateForType == undefined) throw new Error(`No MechanicCityImprovementsTypeState for player ${PlayerColor[player]} and improvementType ${CityImprovementTypes[improvementType]}`)
        return playerCityImprovementsStateForType
    }

    getCurrentCityLevelForPlayerAndType(player: PlayerColor, improvementType: CityImprovementTypes): number | undefined {
        if(this.cityImprovementsState[player] == undefined) throw new Error(`No MechanicCityImprovementsPlayerState for player ${PlayerColor[player]}`)
        const playerCityImprovementsState = this.getPlayerCityImprovementsState(player)
        return playerCityImprovementsState[improvementType]?.level
    }

    getPlayerMetropolisCount(player: PlayerColor): number {
        if(this.cityImprovementsState[player] == undefined) throw new Error(`No MechanicCityImprovementsPlayerState for player ${PlayerColor[player]}`)
        let metropolisCount = 0
        const playerCityImprovementsState = this.getPlayerCityImprovementsState(player)
        for(const cityImprovement of Object.values(playerCityImprovementsState)) {
            if(cityImprovement.hasMetropolis) metropolisCount += 1
        }
        return metropolisCount
    }

    getPlayerColorOfMetropolisOwner(improvementType: CityImprovementTypes): PlayerColor | undefined {
        for(const [playerColor, playerCityImprovementState] of Object.entries(this.cityImprovementsState)) {
            if(playerCityImprovementState == undefined) continue

            for(const [type, state] of Object.entries(playerCityImprovementState)) {
                if(improvementType != parseInt(type, 10)) continue
                if(!state.hasMetropolis) continue

                return parseInt(playerColor, 10)
            }
        }
        return undefined
    }

    getCostForNextCityUpgradeForPlayerAndType(player: PlayerColor, improvementType: CityImprovementTypes): CardEnum[] | undefined {
        const playerCityImprovementsStateForType = this.getPlayerCityImprovementsStateForType(player, improvementType)
        if(playerCityImprovementsStateForType == undefined) return undefined

        const level = playerCityImprovementsStateForType.level
        const discount = playerCityImprovementsStateForType.nextLevelDiscount
        return MechanicValidatorCityImprovements.getCostToUpgradeType(improvementType, level, discount)
    }

    canPlayerUpgradeAnyCity(player: PlayerColor): ActionStatus {
        let canPlayerUpgradeAnyCity: ActionStatus = {canTakeAction: false}
        EnumUtils.loopEnums(CityImprovementTypes, (element, elementNo) => {
            const canPlayerUpgradeCity = this.canPlayerUpgradeCity(player, elementNo)
            if(!canPlayerUpgradeCity.canTakeAction) return

            canPlayerUpgradeAnyCity = canPlayerUpgradeCity
        })
        return canPlayerUpgradeAnyCity
    }

    canPlayerUpgradeCity(player: PlayerColor, cityImprovementType: CityImprovementTypes): ActionStatus {
        const playerController = this.playerValidators.get(player)
        if(playerController == undefined) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.noPlayer'}}

        const placedCityAmount = this.mapValidator.tileState.getPlacedCornerBuildingCountForPlayer(TileCornerBuildingType.City, player)
        const doesPlayerHaveCity = placedCityAmount > 0
        if(!doesPlayerHaveCity) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.needAtLeastOneCity'}}

        const costToUpgradeCity = this.getCostForNextCityUpgradeForPlayerAndType(player, cityImprovementType)
        if(costToUpgradeCity == undefined) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.alreadyMaxLevel'}}

        const doesPlayerHaveEnoughResources = playerController.hasResourceCardsInHand(costToUpgradeCity)
        if(!doesPlayerHaveEnoughResources) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughResources'}}

        const currentCityLevel = this.getCurrentCityLevelForPlayerAndType(playerController.color, cityImprovementType)
        if(currentCityLevel == undefined) return {canTakeAction: false}

        const isCityMaxed = currentCityLevel >= 5
        if(isCityMaxed) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.alreadyMaxLevel'}}

        if(this.isCityUpgradeBlockedBecausePlayerHasNoWhereToPlaceMetropolis(player, cityImprovementType)) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.needAnotherCity'}}

        return {canTakeAction: true}
    }

    static convertCityImprovementTypeToMetropolisType(improvementType: CityImprovementTypes): TileCornerBonusType {
        switch(improvementType) {
            case CityImprovementTypes.Politics: return TileCornerBonusType.MetropolisPolitics
            case CityImprovementTypes.Science: return TileCornerBonusType.MetropolisScience
            case CityImprovementTypes.Trade: return TileCornerBonusType.MetropolisTrade
        }
    }

    private isCityUpgradeBlockedBecausePlayerHasNoWhereToPlaceMetropolis(player: PlayerColor, cityImprovementType: CityImprovementTypes): boolean {
        const playerCityImprovementState = this.getPlayerCityImprovementsState(player)
        if(playerCityImprovementState == undefined) return true

        const currentCityLevel = this.getCurrentCityLevelForPlayerAndType(player, cityImprovementType)
        if(currentCityLevel == undefined) return true

        const placedCityAmount = this.mapValidator.tileState.getPlacedCornerBuildingCountForPlayer(TileCornerBuildingType.City, player)
        const amountOfMetropolisOwned = this.getPlayerMetropolisCount(player)
        const doesPlayerHaveAFreeCity = placedCityAmount - amountOfMetropolisOwned >= 1
        const isCityLevelAtLeastLevel3 = currentCityLevel >= 3
        const doesPlayerHaveMetropolisOfType = this.getPlayerColorOfMetropolisOwner(cityImprovementType) == player
        const isCityUpgradeBlockedBecausePlayerHasNoWhereToPlaceMetropolis = !doesPlayerHaveAFreeCity && isCityLevelAtLeastLevel3 && !doesPlayerHaveMetropolisOfType

        return isCityUpgradeBlockedBecausePlayerHasNoWhereToPlaceMetropolis
    }

    private static getEmptyCityImprovementState(): MechanicCityImprovementsPlayerState {
        return {
            [CityImprovementTypes.Politics]: {level: 0, nextLevelDiscount: 0, hasMetropolis: false},
            [CityImprovementTypes.Science]: {level: 0, nextLevelDiscount: 0, hasMetropolis: false},
            [CityImprovementTypes.Trade]: {level: 0, nextLevelDiscount: 0, hasMetropolis: false},
        }
    }

    private static getCostToUpgradeType(improvementType: CityImprovementTypes, level: number, discount: number = 0): CardEnum[] | undefined {
        const maxLevel = 5
        if(level == maxLevel) return undefined

        const amountOfCardsForNextLevel = level + 1 - discount
        const commodityNeededForCityType = MechanicValidatorCityImprovements.getCommodityRequiredForUpgrade(improvementType)

        const costForNextUpgrade = new Array(amountOfCardsForNextLevel).fill(commodityNeededForCityType)
        return costForNextUpgrade
    }

    private static getCommodityRequiredForUpgrade(improvementType: CityImprovementTypes): CardEnum {
        switch(improvementType) {
            case CityImprovementTypes.Politics: return CardEnum.Coin
            case CityImprovementTypes.Science: return CardEnum.Paper
            case CityImprovementTypes.Trade: return CardEnum.Cloth
        }
    }
}
